import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Check, Circle, CornerUpRight, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { ScanInput } from 'shared/components/scan-input'
import { SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'

/**
 * «Мешки и коробки» — a Bag or a partner's whole Box at the Point: taking it from the
 * driver, opening it, shelving its Parcels (spec §13, §14, §26; ADR-0025, ADR-0037,
 * ADR-0047; tickets 28, 33, 57, 59). A Box taken in whole at the hub rides alone and
 * is worked here exactly as a Bag — the words name which it is (`kind`). The
 * operator scans its code, and the scan takes it — no seal, no weight (ADR-0047).
 * The operator opens the Bag; then each Parcel is scanned onto the shelf. A Parcel of
 * another Point is answered yellow «чужая посылка — пункт B, отложите»: it is not
 * shelved and cannot be handed over here, and one the Bag was not meant to hold is
 * listed as an extra. «Завершить» turns whatever is missing into a red shortage —
 * asked first, since it cannot be undone; who answers for it, support decides. The
 * colour of every answer is the server's.
 *
 * The hub's look: the scan field on top and in focus, the answer under it
 * (`ToneBanner`), then the tables. Scanners type like a keyboard and press Enter.
 *
 * Every call is injected so the behaviour is tested without a backend; they
 * default to the real tRPC client.
 */

export type PointBagRow = RouterOutputs['pointBags']['Incoming'][number]
export type PointBagView = RouterOutputs['pointBags']['Bag']
export type PointBagAcceptRequest = RouterInputs['pointBags']['Accept']
export type PointBagAcceptAnswer = RouterOutputs['pointBags']['Accept']
export type PointBagOpenRequest = RouterInputs['pointBags']['Open']
export type PointBagOpenAnswer = RouterOutputs['pointBags']['Open']
export type PointBagShelveRequest = RouterInputs['pointBags']['Shelve']
export type PointBagShelveAnswer = RouterOutputs['pointBags']['Shelve']
type PointBagOpenRefusal = Extract<PointBagOpenAnswer, { outcome: 'refused' }>['reason']
export type PointBagFinishAnswer = RouterOutputs['pointBags']['Finish']
type RidingKind = PointBagRow['kind']

/**
 * The key of a word that differs for a whole Box (its gender, its noun): `<key>Box`
 * for a partner's Box, `<key>` for a Bag.
 */
const forKind = <K extends string>(key: K, kind: RidingKind) => (kind === 'partner_box' ? `${key}Box` : key) as K

export interface PointBagsProps {
	/** Test seam: the Bags for this Point. */
	list?: () => Promise<PointBagRow[]>
	accept?: (input: PointBagAcceptRequest) => Promise<PointBagAcceptAnswer>
	loadBag?: (containerId: string) => Promise<PointBagView>
	open?: (input: PointBagOpenRequest) => Promise<PointBagOpenAnswer>
	shelve?: (input: PointBagShelveRequest) => Promise<PointBagShelveAnswer>
	finish?: (containerId: string) => Promise<PointBagFinishAnswer>
}

const listViaTrpc = () => trpcClient.pointBags.Incoming.query()
const acceptViaTrpc = (input: PointBagAcceptRequest) => trpcClient.pointBags.Accept.mutate(input)
const loadBagViaTrpc = (containerId: string) => trpcClient.pointBags.Bag.query({ containerId })
const openViaTrpc = (input: PointBagOpenRequest) => trpcClient.pointBags.Open.mutate(input)
const shelveViaTrpc = (input: PointBagShelveRequest) => trpcClient.pointBags.Shelve.mutate(input)
const finishViaTrpc = (containerId: string) => trpcClient.pointBags.Finish.mutate({ containerId })

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/** A Bag still on its way to the Point (at the hub, with the driver); the rest are here. */
const onTheWay = (bag: PointBagRow) => bag.stage === 'at_hub' || bag.stage === 'with_driver'

/** The same tables everywhere on this page: the whole list at once, no paging. */
function useWholeTable<T>(data: T[], columns: ColumnDef<T>[], getRowId: (row: T) => string) {
	return useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId,
		initialState: { pagination: { pageIndex: 0, pageSize: 5000 } },
	}).table
}

export function PointBags({
	list = listViaTrpc,
	accept = acceptViaTrpc,
	loadBag = loadBagViaTrpc,
	open = openViaTrpc,
	shelve = shelveViaTrpc,
	finish = finishViaTrpc,
}: PointBagsProps) {
	const { t } = useTranslate()
	const [bags, setBags] = useState<PointBagRow[] | null>(null)
	const [listError, setListError] = useState<string | null>(null)
	const [working, setWorking] = useState<string | null>(null)

	const refresh = useCallback(() => {
		list().then(
			(rows) => {
				setBags(rows)
				setListError(null)
			},
			(e) => setListError(failure(e)),
		)
	}, [list])

	useEffect(() => {
		refresh()
	}, [refresh])

	if (working) {
		return (
			<BagWork
				key={working}
				containerId={working}
				loadBag={loadBag}
				open={open}
				shelve={shelve}
				finish={finish}
				onDone={() => {
					setWorking(null)
					refresh()
				}}
			/>
		)
	}

	return (
		<div data-testid='point-bags'>
			<PageFrame title='nav.pointBags'>
				<div className='flex flex-col gap-8'>
					<AcceptBag accept={accept} onAccepted={refresh} />
					{listError ? (
						<p className='text-sm text-destructive' data-testid='point-bags-error'>
							{t('pointBags.listFailed', { message: listError })}
						</p>
					) : bags === null ? (
						<p className='text-sm text-muted-foreground'>{t('hub.loading')}</p>
					) : (
						<div className='grid items-start gap-8 lg:grid-cols-2'>
							<BagsTable
								title={t('pointBags.hereTitle')}
								empty={t('pointBags.hereEmpty')}
								testId='point-bags-here'
								bags={bags.filter((b) => !onTheWay(b))}
								onWork={setWorking}
							/>
							<BagsTable
								title={t('pointBags.comingTitle')}
								empty={t('pointBags.comingEmpty')}
								testId='point-bags-coming'
								bags={bags.filter(onTheWay)}
							/>
						</div>
					)}
				</div>
			</PageFrame>
		</div>
	)
}

/** The Bags and whole Boxes of this Point: those here (opened from the row), or those on the way. */
function BagsTable({
	title,
	empty,
	testId,
	bags,
	onWork,
}: {
	title: string
	empty: string
	testId: string
	bags: PointBagRow[]
	/** Opens the Bag — for those already taken from the driver. */
	onWork?: (containerId: string) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<PointBagRow>[]>(
		() => [
			{
				id: 'code',
				header: t('pointBags.colBag'),
				cell: ({ row: { original: bag } }) => (
					<div className='flex items-baseline gap-2'>
						<span className='text-muted-foreground'>{t(`ridingKind.${bag.kind}`)}</span>
						<span className='font-mono font-semibold'>{bag.code}</span>
					</div>
				),
			},
			{
				id: 'parcels',
				header: t('pointBags.colParcels'),
				meta: {
					headClassName: 'text-right',
					cellClassName: 'text-right tabular-nums',
				},
				cell: ({ row }) => row.original.parcelCount,
			},
			...(onWork
				? []
				: [
						{
							id: 'driver',
							header: t('pointBags.colDriver'),
							cell: ({ row }) => row.original.trip?.driverName ?? '—',
						} satisfies ColumnDef<PointBagRow>,
					]),
			{
				id: 'stage',
				header: t('pointBags.colStage'),
				cell: ({ row: { original: bag } }) => (
					<Badge
						variant={bag.stage === 'opened' ? 'default' : bag.stage === 'accepted' ? 'secondary' : 'outline'}
						data-testid={`point-bag-stage-${bag.code}`}
					>
						{t(`pointBagStage.${bag.stage}`)}
					</Badge>
				),
			},
			...(onWork
				? [
						{
							id: 'actions',
							header: '',
							meta: { cellClassName: 'w-px text-right' },
							cell: ({ row: { original: bag } }) => (
								<Button
									size='sm'
									variant={bag.stage === 'accepted' ? 'default' : 'outline'}
									data-testid={`point-bag-work-${bag.code}`}
									onClick={(event) => {
										event.stopPropagation()
										onWork(bag.containerId)
									}}
								>
									{bag.stage === 'accepted' ? t('pointBags.openBag') : t('pointBags.continue')}
								</Button>
							),
						} satisfies ColumnDef<PointBagRow>,
					]
				: []),
		],
		[t, onWork],
	)
	const table = useWholeTable(bags, columns, (bag) => bag.containerId)
	return (
		<section className='flex min-w-0 flex-col gap-2' data-testid={testId}>
			<SectionTitle title={title} count={bags.length} />
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-[480px]'
				emptyText={empty}
				row={{
					attributes: (bag) => ({
						'data-testid': `point-bag-${bag.code}`,
						'data-stage': bag.stage,
						'data-kind': bag.kind,
					}),
					onRowClick: onWork ? (bag) => onWork(bag.containerId) : undefined,
				}}
			/>
		</section>
	)
}

/** Taking the Bag from the driver: the scan of its code is the act — no seal, no weight (ADR-0047). */
function AcceptBag({
	accept,
	onAccepted,
}: {
	accept: (input: PointBagAcceptRequest) => Promise<PointBagAcceptAnswer>
	onAccepted: () => void
}) {
	const { t } = useTranslate()
	const [sending, setSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<PointBagAcceptAnswer | null>(null)
	// Bumped after each answer, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)

	const take = async (code: string) => {
		if (sending) return
		setSending(true)
		setError(null)
		try {
			const res = await accept({ code })
			setAnswer(res)
			if (res.outcome !== 'refused') onAccepted()
		} catch (e) {
			setError(failure(e))
		} finally {
			setSending(false)
			setScanRound((n) => n + 1)
		}
	}

	return (
		<section className='flex flex-col gap-3'>
			<ScanInput key={scanRound} onScan={take} disabled={sending} />
			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid='point-bag-accept-error'>
					{t('pointBags.acceptFailed', { message: error })}
				</p>
			)}
			{answer ? (
				<AcceptBanner answer={answer} />
			) : (
				<p className='text-xs text-muted-foreground'>{t('pointBags.acceptHint')}</p>
			)}
		</section>
	)
}

function AcceptBanner({ answer }: { answer: PointBagAcceptAnswer }) {
	const { t } = useTranslate()
	const ids = {
		testId: 'point-bag-accept-answer',
		titleTestId: 'point-bag-accept-answer-title',
		detailTestId: 'point-bag-accept-answer-detail',
		attributes: { 'data-outcome': answer.outcome },
	}
	switch (answer.outcome) {
		case 'accepted':
			return (
				<ToneBanner
					tone='ready'
					{...ids}
					title={t(forKind('pointBags.acceptedTitle', answer.bag.kind), {
						code: answer.bag.code,
					})}
					detail={t(forKind('pointBags.acceptedDetail', answer.bag.kind), {
						count: String(answer.bag.parcelCount),
					})}
				/>
			)
		case 'already_accepted':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t(forKind('pointBags.alreadyTitle', answer.bag.kind), {
						code: answer.bag.code,
					})}
					detail={t('pointBags.alreadyDetail')}
				/>
			)
		case 'refused':
			return (
				<ToneBanner
					tone='refused'
					{...ids}
					title={t('pointBags.refusedTitle')}
					detail={`${answer.code}: ${t(`pointBagAcceptRefusal.${answer.reason}`)}`}
				/>
			)
	}
}

/** One line of the Bag's table: a Parcel it was meant to hold, or an extra scanned at it. */
interface BagLine {
	parcelId: string
	trackingCode: string
	partnerName: string
	/** `missing` — still unscanned once the Bag is finished: the shortage. */
	status: 'waiting' | 'shelved' | 'set_aside' | 'missing'
	/** Another Point's — set aside for it. */
	otherPoint: string | null
	extra: boolean
}

/** One Bag at work: its opening, the scans onto the shelf, the finish. */
function BagWork({
	containerId,
	loadBag,
	open,
	shelve,
	finish,
	onDone,
}: {
	containerId: string
	loadBag: (containerId: string) => Promise<PointBagView>
	open: (input: PointBagOpenRequest) => Promise<PointBagOpenAnswer>
	shelve: (input: PointBagShelveRequest) => Promise<PointBagShelveAnswer>
	finish: (containerId: string) => Promise<PointBagFinishAnswer>
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [view, setView] = useState<PointBagView | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [openRefusal, setOpenRefusal] = useState<PointBagOpenRefusal | null>(null)
	const [shelveAnswer, setShelveAnswer] = useState<PointBagShelveAnswer | null>(null)
	const [result, setResult] = useState<PointBagFinishAnswer | null>(null)
	const [asking, setAsking] = useState(false)
	// Bumped after every act, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)

	const reload = useCallback(
		() => loadBag(containerId).then(setView, (e) => setError(failure(e))),
		[loadBag, containerId],
	)
	useEffect(() => {
		reload()
	}, [reload])

	const guard = async (act: () => Promise<void>) => {
		if (busy) return
		setBusy(true)
		setError(null)
		try {
			await act()
		} catch (e) {
			setError(failure(e))
		} finally {
			setBusy(false)
			setScanRound((n) => n + 1)
		}
	}

	const doOpen = () =>
		guard(async () => {
			const res = await open({ containerId })
			if (res.outcome === 'refused') setOpenRefusal(res.reason)
			else setOpenRefusal(null)
			await reload()
		})

	const doShelve = (code: string) =>
		guard(async () => {
			setShelveAnswer(await shelve({ containerId, code }))
			await reload()
		})

	const doFinish = () =>
		guard(async () => {
			setResult(await finish(containerId))
			await reload()
		})

	const back = (
		<Button
			variant='ghost'
			size='sm'
			className='-ml-2.5 text-muted-foreground'
			data-testid='point-bag-back'
			onClick={onDone}
		>
			<ArrowLeft />
			{t('pointBags.back')}
		</Button>
	)

	if (!view) {
		return (
			<PageFrame title='nav.pointBags' back={back}>
				<p className='text-sm text-muted-foreground' data-testid='point-bag-loading'>
					{error ? t('pointBags.listFailed', { message: error }) : t('hub.loading')}
				</p>
			</PageFrame>
		)
	}

	const kind = view.kind
	const isOpened = view.stage === 'opened'
	const finished = result !== null || view.finishedAt !== null
	// What the Bag was meant to hold and nobody scanned: the shortage «Завершить» records.
	const unscanned = view.parcels.filter((p) => !p.shelved && p.setAsideFor === null)

	const lines: BagLine[] = [
		...view.parcels.map(
			(p): BagLine => ({
				parcelId: p.parcelId,
				trackingCode: p.trackingCode,
				partnerName: p.partnerName,
				status: p.shelved ? 'shelved' : p.setAsideFor !== null ? 'set_aside' : finished ? 'missing' : 'waiting',
				otherPoint: p.setAsideFor,
				extra: false,
			}),
		),
		...view.extras.map(
			(x): BagLine => ({
				parcelId: x.parcelId,
				trackingCode: x.trackingCode,
				partnerName: x.partnerName,
				status: x.shelved ? 'shelved' : 'set_aside',
				otherPoint: x.shelved ? null : x.ownPointCode,
				extra: true,
			}),
		),
	]

	const onFinish = () => (unscanned.length > 0 ? setAsking(true) : doFinish())

	const actions = !isOpened ? (
		<div data-testid='point-bag-open-step'>
			<Button type='button' data-testid='point-bag-open' disabled={busy} onClick={doOpen}>
				{t(forKind('pointBags.openConfirm', kind))}
			</Button>
		</div>
	) : !finished ? (
		<Button
			type='button'
			// Finishing short records a shortage — it cannot be undone.
			variant={unscanned.length > 0 ? 'destructive' : 'default'}
			data-testid='point-bag-finish'
			disabled={busy}
			onClick={onFinish}
		>
			{t('pointBags.finish')}
		</Button>
	) : undefined

	return (
		<div data-testid='point-bag-work'>
			<PageFrame
				back={back}
				heading={
					<>
						<span>{t(forKind('pointBags.workTitle', kind), { code: view.code })}</span>
						<Badge variant={finished ? 'secondary' : isOpened ? 'default' : 'secondary'} data-testid='point-bag-stage'>
							{finished ? t('pointBags.stageFinished') : t(`pointBagStage.${view.stage}`)}
						</Badge>
					</>
				}
				actions={actions}
			>
				<div className='flex flex-col gap-6'>
					{isOpened && <Counts view={view} />}

					{(openRefusal || (isOpened && !finished) || shelveAnswer || result || error) && (
						<section className='flex flex-col gap-3'>
							{isOpened && !finished && <ScanInput key={scanRound} onScan={doShelve} disabled={busy || asking} />}
							{error && (
								<p role='alert' className='text-sm text-destructive' data-testid='point-bag-error'>
									{t('pointBags.actFailed', { message: error })}
								</p>
							)}
							{openRefusal && (
								<ToneBanner
									tone='refused'
									testId='point-bag-open-refused'
									title={t(forKind('pointBags.openRefusedTitle', kind))}
									detail={t(`pointBagOpenRefusal.${openRefusal}`)}
								/>
							)}
							{result ? (
								<FinishBanner result={result} kind={kind} />
							) : shelveAnswer ? (
								<ShelveBanner answer={shelveAnswer} kind={kind} />
							) : (
								isOpened &&
								!finished && <p className='text-xs text-muted-foreground'>{t(forKind('pointBags.shelveHint', kind))}</p>
							)}
						</section>
					)}

					<ParcelsTable lines={lines} />
				</div>

				<AlertDialog open={asking} onOpenChange={(next) => !next && setAsking(false)}>
					<AlertDialogContent
						data-testid='point-bag-finish-ask'
						onCloseAutoFocus={(event) => {
							// The cursor goes back to the scan field, not to «Завершить».
							event.preventDefault()
							setScanRound((n) => n + 1)
						}}
					>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('pointBags.finishAskTitle', {
									count: String(unscanned.length),
								})}
							</AlertDialogTitle>
							<AlertDialogDescription>{t('pointBags.finishAskBody')}</AlertDialogDescription>
							<ul className='flex flex-col gap-0.5 text-sm' data-testid='point-bag-finish-ask-list'>
								{unscanned.slice(0, 10).map((p) => (
									<li key={p.parcelId} className='font-mono'>
										{p.trackingCode}
									</li>
								))}
								{unscanned.length > 10 && (
									<li className='text-muted-foreground'>
										{t('pointBags.finishAskMore', {
											count: String(unscanned.length - 10),
										})}
									</li>
								)}
							</ul>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>{t('pointBags.finishCancel')}</AlertDialogCancel>
							<AlertDialogAction variant='destructive' data-testid='point-bag-finish-confirm' onClick={doFinish}>
								{t('pointBags.finishConfirm')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</PageFrame>
		</div>
	)
}

/** «На полке N из M», a bar under it, and how many extras turned up. */
function Counts({ view }: { view: PointBagView }) {
	const { t } = useTranslate()
	const { shelved, expected } = view.counts
	return (
		<div className='flex min-w-60 flex-col gap-1.5 self-start'>
			<span className='text-lg leading-tight font-semibold tabular-nums' data-testid='point-bag-counts'>
				{t('pointBags.counts', {
					shelved: String(shelved),
					expected: String(expected),
				})}
				{view.extras.length > 0 && (
					<span className='font-normal text-amber-700 dark:text-amber-400'>
						{' · '}
						{t('pointBags.extrasCount', { count: String(view.extras.length) })}
					</span>
				)}
			</span>
			<div className='h-1.5 overflow-hidden rounded-full bg-muted'>
				<div
					className='h-full rounded-full bg-emerald-500 transition-all'
					style={{ width: `${expected ? (shelved * 100) / expected : 0}%` }}
				/>
			</div>
		</div>
	)
}

const MARK = {
	waiting: <Circle className='size-4 text-muted-foreground/60' aria-hidden />,
	shelved: <Check className='size-4 text-emerald-600' aria-hidden />,
	set_aside: <CornerUpRight className='size-4 text-amber-600' aria-hidden />,
	missing: <X className='size-4 text-destructive' aria-hidden />,
} as const

const AMBER_BADGE = 'border-amber-500/60 text-amber-700 dark:text-amber-400'

/** The Parcels of the Bag, then the extras scanned at it — each with where it is now. */
function ParcelsTable({ lines }: { lines: BagLine[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<BagLine>[]>(
		() => [
			{
				id: 'mark',
				header: '',
				meta: { headClassName: 'w-8', cellClassName: 'w-8' },
				cell: ({ row }) => MARK[row.original.status],
			},
			{
				id: 'code',
				header: t('pointBags.colCode'),
				cell: ({ row: { original: line } }) => (
					<span
						className={cn(
							'font-mono',
							line.status === 'shelved' && 'text-emerald-700 dark:text-emerald-400',
							line.status === 'missing' && 'text-destructive',
						)}
					>
						{line.trackingCode}
					</span>
				),
			},
			{
				id: 'partner',
				header: t('pointBags.colPartner'),
				cell: ({ row }) => row.original.partnerName,
			},
			{
				id: 'status',
				header: t('pointBags.colStatus'),
				cell: ({ row: { original: line } }) => (
					<div className='flex flex-wrap items-center gap-1.5'>
						{line.status === 'shelved' ? (
							<Badge variant='secondary'>{t('pointBags.statusShelved')}</Badge>
						) : line.status === 'set_aside' ? (
							<Badge variant='outline' className={AMBER_BADGE}>
								{t('pointBags.statusSetAside', {
									point: line.otherPoint ?? '',
								})}
							</Badge>
						) : line.status === 'missing' ? (
							<Badge variant='destructive'>{t('pointBags.statusMissing')}</Badge>
						) : (
							<Badge variant='outline'>{t('pointBags.statusWaiting')}</Badge>
						)}
						{line.extra && (
							<Badge variant='outline' className={AMBER_BADGE}>
								{t('pointBags.statusExtra')}
							</Badge>
						)}
					</div>
				),
			},
		],
		[t],
	)
	const table = useWholeTable(lines, columns, (line) => line.parcelId)
	return (
		<section className='flex min-w-0 flex-col gap-2'>
			<SectionTitle title={t('pointBags.parcelsTitle')} count={lines.length} />
			<DataTable
				table={table}
				withPagination={false}
				emptyText={t('pointBags.parcelsEmpty')}
				row={{
					attributes: (line) =>
						line.extra
							? {
									'data-testid': `point-bag-extra-${line.trackingCode}`,
									'data-status': line.status,
								}
							: {
									'data-testid': `point-bag-parcel-${line.trackingCode}`,
									'data-status': line.status,
									'data-shelved': line.status === 'shelved',
									'data-set-aside': line.status === 'set_aside',
								},
				}}
			/>
		</section>
	)
}

function ShelveBanner({ answer, kind }: { answer: PointBagShelveAnswer; kind: RidingKind }) {
	const { t } = useTranslate()
	const ids = {
		testId: 'point-bag-shelve-answer',
		titleTestId: 'point-bag-shelve-answer-title',
		detailTestId: 'point-bag-shelve-answer-detail',
		attributes: { 'data-outcome': answer.outcome },
	}
	const progress = t('pointBags.counts', {
		shelved: String(answer.counts.shelved),
		expected: String(answer.counts.expected),
	})
	switch (answer.outcome) {
		case 'shelved':
			return (
				<ToneBanner
					tone='ready'
					{...ids}
					title={t('pointBags.shelvedTitle', { code: answer.trackingCode })}
					detail={progress}
				/>
			)
		case 'set_aside':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.setAsideTitle', { point: answer.ownPointCode })}
					detail={[
						answer.trackingCode,
						t('pointBags.setAsideDetail'),
						answer.extra ? t(forKind('pointBags.setAsideExtra', kind)) : '',
					]
						.filter(Boolean)
						.join(' · ')}
				/>
			)
		case 'shelved_extra':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.shelvedExtraTitle', {
						code: answer.trackingCode,
					})}
					detail={[t(forKind('pointBags.shelvedExtraDetail', kind)), progress].join(' · ')}
				/>
			)
		case 'already_set_aside':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.alreadySetAsideTitle', {
						code: answer.trackingCode,
					})}
					detail={t('pointBags.setAsideMark', { point: answer.ownPointCode })}
				/>
			)
		case 'already_shelved':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.alreadyShelvedTitle', {
						code: answer.trackingCode,
					})}
					detail={progress}
				/>
			)
		case 'found':
			// A Parcel declared lost, found by this scan (ticket 43): on this shelf, or set aside.
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={
						answer.where === 'own_point'
							? t('pointBags.foundTitle', { code: answer.trackingCode })
							: t('pointBags.foundOtherTitle', { point: answer.ownPointCode })
					}
					detail={[answer.trackingCode, t('pointBags.foundDetail')].join(' · ')}
				/>
			)
		case 'refused':
			return (
				<ToneBanner
					tone='refused'
					{...ids}
					title={t('pointBags.shelveRefusedTitle')}
					detail={`${answer.code}: ${
						answer.reason === 'sealed_elsewhere' && answer.sealedIn
							? t(answer.otherPoint ? 'pointBags.sealedElsewhereOther' : 'pointBags.sealedElsewhereIn', {
									bag: answer.sealedIn,
								})
							: t(`pointBagShelveRefusal.${answer.reason}`)
					}`}
				/>
			)
	}
}

/**
 * Green when everything matched; yellow with extras only; red with the missing
 * Parcels and who answers. The extras — another Point's, set aside, or this
 * Point's, put on the shelf — are named either way.
 */
function FinishBanner({ result, kind }: { result: PointBagFinishAnswer; kind: RidingKind }) {
	const { t } = useTranslate()
	const ids = {
		testId: 'point-bag-finish-answer',
		titleTestId: 'point-bag-finish-answer-title',
		detailTestId: 'point-bag-finish-answer-detail',
	}
	const extras = result.extras.length > 0 && (
		<div className='text-sm font-medium' data-testid='point-bag-finish-extras'>
			{t('pointBags.extrasCount', { count: String(result.extras.length) })}:{' '}
			{result.extras
				.map(
					(x) =>
						`${x.trackingCode} — ${
							x.shelved ? t('pointBags.extraShelvedMark') : t('pointBags.setAsideMark', { point: x.ownPointCode })
						}`,
				)
				.join('; ')}
		</div>
	)
	if (result.tone === 'ready' || result.tone === 'attention') {
		return (
			<ToneBanner
				tone={result.tone}
				{...ids}
				title={t(forKind('pointBags.finishedTitle', kind))}
				detail={t('pointBags.finishedDetail', {
					count: String(result.counts.shelved),
				})}
			>
				{extras}
			</ToneBanner>
		)
	}
	return (
		<ToneBanner
			tone='refused'
			{...ids}
			title={t('pointBags.shortageTitle', {
				count: String(result.shortage.length),
			})}
			detail={t('pointBags.shortageSupportDecides')}
		>
			<div className='font-mono text-sm font-medium' data-testid='point-bag-shortage'>
				{result.shortage.map((s) => s.trackingCode).join(', ')}
			</div>
			{extras}
		</ToneBanner>
	)
}
